import {
  Badge,
  Button,
  Card,
  EmptyState,
  FreshnessBadge,
  Modal,
  PageHeader,
  PartialState,
  Skeleton,
} from "@evolua/ui";
import { useState } from "react";
import {
  DEMO_FLAG_KEYS,
  useFeatureFlagAudit,
  useFeatureFlags,
  useSetFeatureFlag,
} from "../api/use-feature-flags.js";
import { useSession } from "../session/SessionContext.js";

const MANAGED_FLAGS = [
  {
    key: DEMO_FLAG_KEYS.employeeNotesEnabled,
    label: "Notas de colaborador",
    description: "Controla POST /v1/employees/:id/notes e o formulário em Pessoas.",
  },
  {
    key: DEMO_FLAG_KEYS.vacationRequestEnabled,
    label: "Solicitação de férias",
    description: "Reservada para FASE 6 — toggle só para demonstrar admin.",
  },
] as const;

export function FeatureFlagsPage() {
  const { session, isDemoMode } = useSession();
  const flags = useFeatureFlags();
  const audit = useFeatureFlagAudit();
  const setFlag = useSetFeatureFlag();
  const tenantId = session?.companyId ?? "—";
  const [pendingDisableKey, setPendingDisableKey] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Feature flags"
        lead={`Escopo tenant: ${tenantId}${
          isDemoMode
            ? " (demo — troca a empresa no cabeçalho para ver overrides por tenant)."
            : " (API)."
        }`}
        freshness={
          <FreshnessBadge
            syncedAt={flags.dataUpdatedAt ? new Date(flags.dataUpdatedAt) : null}
          />
        }
      />

      {isDemoMode ? (
        <PartialState description="Em demo as flags ficam em sessionStorage por empresa. Com API real, PUT /v1/feature-flags/:key + auditoria." />
      ) : null}

      <div className="evolua-stack evolua-stack--3">
        {MANAGED_FLAGS.map((item) => {
          const enabled = flags.data?.flags[item.key] ?? false;
          return (
            <Card key={item.key} title={item.label}>
              <p>{item.description}</p>
              <p>
                <code>{item.key}</code>
                <Badge tone={enabled ? "success" : "neutral"}>
                  {enabled ? "ligada" : "desligada"}
                </Badge>
              </p>
              <Button
                type="button"
                variant={enabled ? "danger" : "primary"}
                disabled={setFlag.isPending || flags.isPending}
                onClick={() => {
                  if (enabled) {
                    setPendingDisableKey(item.key);
                    return;
                  }
                  void setFlag.mutateAsync({ key: item.key, enabled: true });
                }}
              >
                {enabled ? "Desligar neste tenant" : "Ligar neste tenant"}
              </Button>
            </Card>
          );
        })}
      </div>

      <h2 className="evolua-section-title">Auditoria recente</h2>
      {audit.isPending ? <Skeleton height="6rem" label="Auditoria de flags" /> : null}
      {!audit.isPending && (audit.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Nenhuma alteração"
          description="Ainda não há mudanças de feature flags registradas neste tenant."
        />
      ) : null}
      {!audit.isPending && (audit.data?.data.length ?? 0) > 0 ? (
        <ul className="evolua-stack evolua-stack--3">
          {(audit.data?.data ?? []).slice(0, 10).map((entry) => (
            <li key={entry.id}>
              <code>{entry.flagKey}</code> → {entry.nextEnabled ? "on" : "off"} ·{" "}
              {entry.actor} · {new Date(entry.at).toLocaleString("pt-BR")}
              {entry.targetId ? ` · ${entry.targetId}` : ""}
            </li>
          ))}
        </ul>
      ) : null}

      <Modal
        open={Boolean(pendingDisableKey)}
        title="Desligar neste tenant"
        onClose={() => setPendingDisableKey(null)}
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPendingDisableKey(null)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={setFlag.isPending}
              onClick={() => {
                if (!pendingDisableKey) return;
                const key = pendingDisableKey;
                setPendingDisableKey(null);
                void setFlag.mutateAsync({ key, enabled: false });
              }}
            >
              Desligar neste tenant
            </Button>
          </>
        }
      >
        <p>A flag deixa de valer para este tenant. Confirma desligar?</p>
      </Modal>
    </>
  );
}
